import { useEffect, useState } from 'react';
import { Screen } from '../../app/Frame';
import { useMeContext, useWritable } from '../../app/MeContext';
import { useScreen, useSheet, useToast } from '../../app/shell-context';
import { ApiError } from '../../api/client';
import { formatDateTime, weekdayRu } from '../../lib/time';
import { formatKop } from '../../lib/money';
import { plural, pluralWithCount } from '../../lib/plural';
import { Notice } from '../../ui/parts';
import { ChevronIcon } from '../../ui/icons';
import { useSaveSchedule, useSchedule, useValidateSchedule } from '../api';
import type { Conflict, Rate, WorkRule } from '../types';
import { toRows, toRules, type HourRow as Row } from '../schedule-rows';

function patchRow(setRows: React.Dispatch<React.SetStateAction<Row[] | null>>, i: number, p: Partial<Row>) {
  setRows((prev) => (prev ? prev.map((r, k) => (k === i ? { ...r, ...p } : r)) : prev));
}


/**
 * Доля специалиста в процентах. У фиксированных условий её не хранят, поэтому
 * считаем от цены: без этого одна строка списка выглядит сломанной рядом с теми,
 * где процент есть.
 */
const payoutPercent = (r: Rate): number | null => {
  if (r.payout_mode === 'percent' && r.payout_percent !== null) return r.payout_percent;
  return r.price > 0 ? Math.round((r.payout / r.price) * 100) : null;
};

/**
 * Вкладка «Расписание»: рабочие часы, услуги и правила записи.
 * Перед сохранением спрашиваем сервер, что из будущих записей в неё не влезает —
 * молча уронить чужой приём нельзя.
 */
export function Hours() {
  const me = useMeContext();
  const writable = useWritable();
  const sheet = useSheet();
  const toast = useToast();
  const schedule = useSchedule();
  const validate = useValidateSchedule();
  const save = useSaveSchedule();

  const [rows, setRows] = useState<Row[] | null>(null);
  const [rules, setRules] = useState<{ buffer: number; horizon: number; lead: number } | null>(null);

  useEffect(() => {
    if (!schedule.data || rows) return;
    setRows(toRows(schedule.data.rules));
    setRules({
      buffer: schedule.data.settings.buffer_min,
      horizon: schedule.data.settings.horizon_days,
      lead: schedule.data.settings.min_lead_hours,
    });
  }, [schedule.data, rows]);

  const busy = validate.isPending || save.isPending;

  // Раздел с вкладками: сохранение стоит внутри экрана, как в прототипе.
  useScreen('Расписание', null);

  function submit() {
    if (!rows || !rules) return;
    const next = toRules(rows);
    validate.mutate(next, {
      onSuccess: ({ conflicts }) => {
        if (conflicts.length) {
          sheet.open(<ConflictsSheet conflicts={conflicts} onConfirm={() => doSave(next)} />);
          return;
        }
        doSave(next);
      },
      onError: (e) => toast(e instanceof ApiError ? e.message : 'Не получилось проверить сетку'),
    });
  }

  function doSave(next: WorkRule[]) {
    if (!rules) return;
    save.mutate(
      { rules: next, bufferMin: rules.buffer, horizonDays: rules.horizon, minLeadHours: rules.lead },
      {
        onSuccess: () => {
          sheet.close();
          toast('Расписание сохранено');
        },
        onError: (e) => toast(e instanceof ApiError ? e.message : 'Не получилось сохранить'),
      },
    );
  }

  if (schedule.isPending || !rows || !rules) {
    return (
      <Screen>
        <p className="lead">Загружаем расписание…</p>
      </Screen>
    );
  }

  const rates = schedule.data?.rates ?? [];

  return (
    <Screen>
      <div>
        <p className="eyebrow">Рабочие часы</p>
        <p className="lead" style={{ marginTop: 6 }}>
          Часы, в которые вы принимаете. По ним клиенты видят свободное время для записи.
        </p>
      </div>

      <div className="hours">
        {rows.map((r, i) => (
          <div className={`hrow${r.on ? '' : ' is-off'}`} key={r.weekday}>
            <span className="wd">{weekdayRu(r.weekday)}</span>
            <span className="times">
              <input
                value={r.from}
                size={5}
                aria-label="начало"
                disabled={!writable}
                onChange={(e) => patchRow(setRows, i, { from: e.target.value })}
              />
              {' — '}
              <input
                value={r.to}
                size={5}
                aria-label="конец"
                disabled={!writable}
                onChange={(e) => patchRow(setRows, i, { to: e.target.value })}
              />
            </span>
            <button
              className="switch"
              type="button"
              aria-pressed={r.on}
              aria-label={`${weekdayRu(r.weekday, 'long')} рабочий день`}
              disabled={!writable}
              onClick={() => patchRow(setRows, i, { on: !r.on })}
            />
          </div>
        ))}
      </div>

      {/* Выключенные дни гасятся стилем, но серые 10:00 – 18:00 всё равно читаются как
          настроенные. Пока не включён ни один день, записаться нельзя вообще — говорим прямо. */}
      {rows.every((r) => !r.on) ? (
        <Notice>Ни один день не включён — записаться к вам сейчас нельзя.</Notice>
      ) : null}

      <p className="eyebrow" style={{ marginTop: 4 }}>Мои услуги и оплата</p>
      {/* Ограничение стоит ДО списка: иначе его читают уже после поисков кнопки «изменить». */}
      <p className="lead">Услуги и суммы настраивает администратор организации.</p>
      <div className="rows">
        {rates.filter((r) => r.is_active).map((r) => {
          const pct = payoutPercent(r);
          return (
            <div className="rw" key={r.service_id}>
              {/* Раскладка строки — как в «Услугах и ставках» у администратора: название
                  отдельно, длительность и формат подстрокой. Длинные названия вроде
                  «Заключение по результатам нейродиагностики» иначе разъезжаются на
                  четыре строки и выдавливают цену. */}
              <span className="rw-body">
                <b>{r.title}</b>
                <span>{r.duration_min} мин · {r.is_online ? 'онлайн' : 'очно'}</span>
              </span>
              <span className="rw-num">
                <b>{formatKop(r.price)}</b>
                <span className="pay">вам — {formatKop(r.payout)}{pct === null ? '' : ` (${pct} %)`}</span>
              </span>
            </div>
          );
        })}
        {rates.filter((r) => r.is_active).length === 0 ? (
          <p className="lead">Услуги пока не назначены — это делает администратор.</p>
        ) : null}
      </div>

      <p className="eyebrow" style={{ marginTop: 4 }}>Правила записи</p>
      <div className="kv kv-wrap">
        <RuleRow
          label="Перерыв между записями"
          value={`${rules.buffer} мин`}
          editable={writable}
          onEdit={() => sheet.open(
            <NumberSheet
              title="Перерыв между записями"
              hint="Столько времени остаётся между двумя записями: на заметки, проветривание и дорогу клиента."
              unit="мин"
              value={rules.buffer}
              min={0}
              max={120}
              onSave={(v) => setRules({ ...rules, buffer: v })}
            />,
          )}
        />
        <RuleRow
          label="Запись открыта на"
          value={`${rules.horizon} ${plural(rules.horizon, 'день', 'дня', 'дней')} вперёд`}
          editable={writable}
          onEdit={() => sheet.open(
            <NumberSheet
              title="Горизонт записи"
              hint="На сколько дней вперёд клиенты видят свободное время для записи."
              unit="дней"
              value={rules.horizon}
              min={1}
              max={180}
              onSave={(v) => setRules({ ...rules, horizon: v })}
            />,
          )}
        />
        <RuleRow
          label="Записаться не позже чем"
          value={`за ${rules.lead} ${plural(rules.lead, 'час', 'часа', 'часов')} до начала`}
          editable={writable}
          onEdit={() => sheet.open(
            <NumberSheet
              title="Запас перед записью"
              hint="Записаться впритык нельзя: столько часов должно остаться до начала."
              unit="часов"
              value={rules.lead}
              min={0}
              max={72}
              onSave={(v) => setRules({ ...rules, lead: v })}
            />,
          )}
        />
      </div>

      {me.readOnly ? <Notice>Подписка организации приостановлена — расписание можно смотреть, но не менять.</Notice> : null}

      {writable ? (
        <button className="btn solid" disabled={busy} onClick={() => submit()}>
          {busy ? 'Проверяем…' : 'Сохранить'}
        </button>
      ) : null}
    </Screen>
  );
}

/**
 * Строка правила. `kv-row` — штатный паттерн настроечной строки («значение целиком
 * тап-зона», org.css): шеврон вместо слова «изменить». Слово занимало 73px из 315
 * доступных, и на телефоне строка не помещалась даже с коротким значением —
 * «изменить» уезжало под значение, а длинное значение ломалось пополам.
 *
 * Значение — отдельным элементом, а не голым текстом: `.kv .v > *` держит nowrap
 * только на элементах.
 */
function RuleRow({ label, value, editable, onEdit }: { label: string; value: string; editable: boolean; onEdit: () => void }) {
  if (!editable) {
    return (
      <div className="kv-row">
        <span className="k">{label}</span>
        <span className="v"><span>{value}</span></span>
      </div>
    );
  }
  return (
    <button className="kv-row" type="button" onClick={onEdit}>
      <span className="k">{label}</span>
      <span className="v">
        <span>{value}</span>
        <span className="chev"><ChevronIcon /></span>
      </span>
    </button>
  );
}

/** Что не влезает в новую сетку — показываем до сохранения, по одной записи. */
function ConflictsSheet({ conflicts, onConfirm }: { conflicts: Conflict[]; onConfirm: () => void }) {
  const me = useMeContext();
  const sheet = useSheet();
  return (
    <>
      <h3>{pluralWithCount(conflicts.length, 'запись не влезает', 'записи не влезают', 'записей не влезают')} в новую сетку</h3>
      <p>
        Сетку сохранить можно — уже созданные записи никуда не денутся и останутся в вашем дне.
        Но новых окон в это время не будет, поэтому лучше перенести их заранее.
      </p>
      <div className="rows">
        {conflicts.slice(0, 8).map((c) => (
          <div className="rw" key={c.appointmentId}>
            <span className="rw-body">
              <b>{formatDateTime(c.startsAt, me.center.tz, me.center.timeFormat)}</b>
              <span>{c.childName} · {c.reason}</span>
            </span>
          </div>
        ))}
      </div>
      {conflicts.length > 8 ? (
        <p className="footnote" style={{ textAlign: 'left' }}>…и ещё {conflicts.length - 8} по той же причине</p>
      ) : null}
      <div className="row-actions">
        <button className="btn" onClick={() => sheet.close()}>Вернуться к сетке</button>
        <button className="btn solid" onClick={onConfirm}>Сохранить всё равно</button>
      </div>
    </>
  );
}

function NumberSheet({ title, hint, unit, value, min, max, onSave }: {
  title: string;
  hint: string;
  unit: string;
  value: number;
  min: number;
  max: number;
  onSave: (v: number) => void;
}) {
  const sheet = useSheet();
  const [v, setV] = useState(String(value));
  const n = Number(v);
  const ok = Number.isInteger(n) && n >= min && n <= max;

  return (
    <>
      <h3>{title}</h3>
      <p>{hint}</p>
      <div className="field">
        <label htmlFor="numVal">Значение</label>
        <div className="suffix">
          <input id="numVal" type="number" min={min} max={max} inputMode="numeric" value={v} onChange={(e) => setV(e.target.value)} />
          <i>{unit}</i>
        </div>
        <span className="hint">Изменение вступит в силу, когда сохраните расписание.</span>
      </div>
      <div className="row-actions">
        <button className="btn" onClick={() => sheet.close()}>Отмена</button>
        <button
          className="btn solid"
          disabled={!ok}
          onClick={() => {
            onSave(n);
            sheet.close();
          }}
        >
          Готово
        </button>
      </div>
    </>
  );
}
